<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TicketsAPI from 'dashboard/api/tickets';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  ticket: { type: Object, required: true },
  ticketType: { type: Object, required: true },
});

const emit = defineEmits(['close', 'saved']);

const { t } = useI18n();
const selectedStateId = ref(props.ticket.ticket_state.id);
const isSaving = ref(false);
const resolution = ref({
  outcome: 'solved',
  summary: '',
  root_cause: '',
  follow_up: '',
  reopen_reason: '',
});

watch(
  () => props.ticket,
  ticket => {
    selectedStateId.value = ticket.ticket_state.id;
    resolution.value = {
      outcome: 'solved',
      summary: '',
      root_cause: '',
      follow_up: '',
      reopen_reason: '',
    };
  }
);

const states = computed(() => props.ticketType.states || []);
const selectedState = computed(() =>
  states.value.find(state => Number(state.id) === Number(selectedStateId.value))
);
const currentState = computed(() => props.ticket.ticket_state);
const isResolving = computed(
  () => selectedState.value?.terminal && !currentState.value.terminal
);
const isReopening = computed(
  () => !selectedState.value?.terminal && currentState.value.terminal
);
const hasStateChange = computed(
  () => Number(selectedStateId.value) !== Number(currentState.value.id)
);
const canSave = computed(() => {
  if (!hasStateChange.value) return false;
  if (isResolving.value) {
    return Boolean(resolution.value.outcome && resolution.value.summary.trim());
  }
  if (isReopening.value) return Boolean(resolution.value.reopen_reason.trim());
  return true;
});
const resolutions = computed(() =>
  [...(props.ticket.resolutions || [])].reverse()
);
const events = computed(() => [...(props.ticket.events || [])].reverse());

const outcomes = computed(() => [
  { value: 'solved', label: t('TICKET_LIFECYCLE.OUTCOMES.SOLVED') },
  {
    value: 'answer_provided',
    label: t('TICKET_LIFECYCLE.OUTCOMES.ANSWER_PROVIDED'),
  },
  {
    value: 'work_completed',
    label: t('TICKET_LIFECYCLE.OUTCOMES.WORK_COMPLETED'),
  },
  { value: 'duplicate', label: t('TICKET_LIFECYCLE.OUTCOMES.DUPLICATE') },
  {
    value: 'no_response',
    label: t('TICKET_LIFECYCLE.OUTCOMES.NO_RESPONSE'),
  },
  { value: 'canceled', label: t('TICKET_LIFECYCLE.OUTCOMES.CANCELED') },
  { value: 'spam', label: t('TICKET_LIFECYCLE.OUTCOMES.SPAM') },
  { value: 'other', label: t('TICKET_LIFECYCLE.OUTCOMES.OTHER') },
]);

const formatTimestamp = value =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));

const stateName = id =>
  states.value.find(state => Number(state.id) === Number(id))?.name ||
  t('TICKET_LIFECYCLE.UNKNOWN_STATE');

const outcomeLabel = outcome =>
  outcomes.value.find(item => item.value === outcome)?.label ||
  t('TICKET_LIFECYCLE.OUTCOMES.OTHER');

const eventDescription = event => {
  const actor = event.actor?.name || t('TICKET_LIFECYCLE.SYSTEM');
  if (event.event_type === 'created') {
    return t('TICKET_LIFECYCLE.EVENTS.CREATED', { actor });
  }
  if (event.event_type === 'state_changed') {
    return t('TICKET_LIFECYCLE.EVENTS.STATE_CHANGED', {
      actor,
      from: stateName(event.data.from),
      to: stateName(event.data.to),
    });
  }
  if (event.event_type === 'assignment_changed') {
    return t('TICKET_LIFECYCLE.EVENTS.ASSIGNMENT_CHANGED', { actor });
  }
  if (event.event_type === 'visibility_changed') {
    return t('TICKET_LIFECYCLE.EVENTS.VISIBILITY_CHANGED', { actor });
  }
  if (event.event_type === 'resolution_recorded') {
    return t('TICKET_LIFECYCLE.EVENTS.RESOLUTION_RECORDED', { actor });
  }
  if (event.event_type === 'reopened') {
    return t('TICKET_LIFECYCLE.EVENTS.REOPENED', { actor });
  }
  return t('TICKET_LIFECYCLE.EVENTS.UPDATED', { actor });
};

const primaryLabel = computed(() => {
  if (isResolving.value) return t('TICKET_LIFECYCLE.RESOLVE');
  if (isReopening.value) return t('TICKET_LIFECYCLE.REOPEN');
  return t('TICKET_LIFECYCLE.UPDATE_STATE');
});

const save = async () => {
  isSaving.value = true;
  const payload = { ticket_state_id: selectedStateId.value };
  if (isResolving.value) {
    payload.resolution = {
      outcome: resolution.value.outcome,
      summary: resolution.value.summary,
      root_cause: resolution.value.root_cause,
      follow_up: resolution.value.follow_up,
    };
  } else if (isReopening.value) {
    payload.resolution = { reopen_reason: resolution.value.reopen_reason };
  }

  try {
    const { data } = await TicketsAPI.update(props.ticket.id, {
      ticket: payload,
    });
    emit('saved', data);
    useAlert(t('TICKET_LIFECYCLE.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('TICKET_LIFECYCLE.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex justify-end bg-n-alpha-4"
    @click.self="emit('close')"
  >
    <section
      class="flex h-full w-full max-w-xl flex-col bg-n-surface-1 shadow-xl"
      :aria-label="$t('TICKET_LIFECYCLE.TITLE')"
    >
      <header
        class="flex items-start justify-between gap-4 border-b border-n-weak px-5 py-4"
      >
        <div class="min-w-0">
          <div class="mb-1 flex items-center gap-2 text-xs text-n-slate-10">
            <span>{{ ticket.ticket_type.name }}</span>
            <span>{{ $t('TICKET_LIFECYCLE.SEPARATOR') }}</span>
            <span>{{ ticket.ticket_state.name }}</span>
          </div>
          <h2 class="m-0 truncate text-lg font-semibold text-n-slate-12">
            {{ ticket.title }}
          </h2>
          <p
            v-if="ticket.description"
            class="mb-0 mt-1 text-sm text-n-slate-10"
          >
            {{ ticket.description }}
          </p>
        </div>
        <Button
          icon="i-lucide-x"
          variant="ghost"
          color="slate"
          type="button"
          @click="emit('close')"
        />
      </header>

      <div class="flex-1 space-y-6 overflow-y-auto px-5 py-5">
        <section class="space-y-3">
          <div>
            <h3 class="m-0 text-sm font-semibold text-n-slate-12">
              {{ $t('TICKET_LIFECYCLE.NEXT_STATE') }}
            </h3>
            <p class="mb-0 mt-1 text-xs text-n-slate-10">
              {{ $t('TICKET_LIFECYCLE.NEXT_STATE_DESCRIPTION') }}
            </p>
          </div>
          <select
            v-model="selectedStateId"
            class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
          >
            <option v-for="state in states" :key="state.id" :value="state.id">
              {{ state.name }}
            </option>
          </select>
        </section>

        <section
          v-if="isResolving"
          class="space-y-4 border-y border-n-weak bg-n-alpha-1 px-4 py-4"
        >
          <div class="flex gap-3">
            <span
              class="flex size-8 shrink-0 items-center justify-center rounded-full bg-n-teal-3"
            >
              <Icon
                icon="i-lucide-circle-check"
                class="size-4 text-n-teal-11"
              />
            </span>
            <div>
              <h3 class="m-0 text-sm font-semibold text-n-slate-12">
                {{ $t('TICKET_LIFECYCLE.RESOLUTION_TITLE') }}
              </h3>
              <p class="mb-0 mt-1 text-xs text-n-slate-10">
                {{ $t('TICKET_LIFECYCLE.RESOLUTION_DESCRIPTION') }}
              </p>
            </div>
          </div>

          <label class="block space-y-1.5 text-sm text-n-slate-11">
            <span>{{ $t('TICKET_LIFECYCLE.OUTCOME') }}</span>
            <select
              v-model="resolution.outcome"
              required
              class="h-10 w-full rounded-md border border-n-weak bg-n-surface-1 px-3 text-n-slate-12"
            >
              <option
                v-for="outcome in outcomes"
                :key="outcome.value"
                :value="outcome.value"
              >
                {{ outcome.label }}
              </option>
            </select>
          </label>

          <label class="block space-y-1.5 text-sm text-n-slate-11">
            <span>{{ $t('TICKET_LIFECYCLE.SUMMARY') }}</span>
            <textarea
              v-model="resolution.summary"
              required
              rows="4"
              maxlength="5000"
              :placeholder="$t('TICKET_LIFECYCLE.SUMMARY_PLACEHOLDER')"
              class="w-full resize-none rounded-md border border-n-weak bg-n-surface-1 px-3 py-2 text-n-slate-12 outline-none focus:border-n-brand"
            />
          </label>

          <label class="block space-y-1.5 text-sm text-n-slate-11">
            <span>{{ $t('TICKET_LIFECYCLE.ROOT_CAUSE') }}</span>
            <textarea
              v-model="resolution.root_cause"
              rows="3"
              maxlength="5000"
              :placeholder="$t('TICKET_LIFECYCLE.ROOT_CAUSE_PLACEHOLDER')"
              class="w-full resize-none rounded-md border border-n-weak bg-n-surface-1 px-3 py-2 text-n-slate-12 outline-none focus:border-n-brand"
            />
          </label>

          <label class="block space-y-1.5 text-sm text-n-slate-11">
            <span>{{ $t('TICKET_LIFECYCLE.FOLLOW_UP') }}</span>
            <textarea
              v-model="resolution.follow_up"
              rows="3"
              maxlength="5000"
              :placeholder="$t('TICKET_LIFECYCLE.FOLLOW_UP_PLACEHOLDER')"
              class="w-full resize-none rounded-md border border-n-weak bg-n-surface-1 px-3 py-2 text-n-slate-12 outline-none focus:border-n-brand"
            />
          </label>
        </section>

        <section
          v-if="isReopening"
          class="space-y-3 border-y border-n-weak bg-n-alpha-1 px-4 py-4"
        >
          <div>
            <h3 class="m-0 text-sm font-semibold text-n-slate-12">
              {{ $t('TICKET_LIFECYCLE.REOPEN_TITLE') }}
            </h3>
            <p class="mb-0 mt-1 text-xs text-n-slate-10">
              {{ $t('TICKET_LIFECYCLE.REOPEN_DESCRIPTION') }}
            </p>
          </div>
          <textarea
            v-model="resolution.reopen_reason"
            required
            rows="3"
            maxlength="5000"
            :placeholder="$t('TICKET_LIFECYCLE.REOPEN_PLACEHOLDER')"
            class="w-full resize-none rounded-md border border-n-weak bg-n-surface-1 px-3 py-2 text-n-slate-12 outline-none focus:border-n-brand"
          />
        </section>

        <section class="space-y-3">
          <div>
            <h3 class="m-0 text-sm font-semibold text-n-slate-12">
              {{ $t('TICKET_LIFECYCLE.RESOLUTION_HISTORY') }}
            </h3>
            <p class="mb-0 mt-1 text-xs text-n-slate-10">
              {{ $t('TICKET_LIFECYCLE.RESOLUTION_HISTORY_DESCRIPTION') }}
            </p>
          </div>

          <article
            v-for="item in resolutions"
            :key="item.id"
            class="border-l-2 border-n-teal-8 bg-n-alpha-1 px-4 py-3"
          >
            <div class="flex flex-wrap items-center justify-between gap-2">
              <span class="text-sm font-semibold text-n-slate-12">
                {{ outcomeLabel(item.outcome) }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{ formatTimestamp(item.created_at) }}
              </span>
            </div>
            <p class="mb-0 mt-2 whitespace-pre-wrap text-sm text-n-slate-11">
              {{ item.summary }}
            </p>
            <dl class="mb-0 mt-3 space-y-2 text-xs">
              <div v-if="item.root_cause">
                <dt class="font-medium text-n-slate-10">
                  {{ $t('TICKET_LIFECYCLE.ROOT_CAUSE') }}
                </dt>
                <dd class="m-0 whitespace-pre-wrap text-n-slate-11">
                  {{ item.root_cause }}
                </dd>
              </div>
              <div v-if="item.follow_up">
                <dt class="font-medium text-n-slate-10">
                  {{ $t('TICKET_LIFECYCLE.FOLLOW_UP') }}
                </dt>
                <dd class="m-0 whitespace-pre-wrap text-n-slate-11">
                  {{ item.follow_up }}
                </dd>
              </div>
            </dl>
            <p class="mb-0 mt-3 text-xs text-n-slate-10">
              {{
                $t('TICKET_LIFECYCLE.RESOLVED_BY', {
                  name: item.resolved_by?.name || $t('TICKET_LIFECYCLE.SYSTEM'),
                })
              }}
            </p>
            <div
              v-if="item.reopened_at"
              class="mt-3 border-t border-n-weak pt-3 text-xs text-n-slate-11"
            >
              <p class="mb-1 font-medium text-n-slate-12">
                {{
                  $t('TICKET_LIFECYCLE.REOPENED_BY', {
                    name:
                      item.reopened_by?.name || $t('TICKET_LIFECYCLE.SYSTEM'),
                    date: formatTimestamp(item.reopened_at),
                  })
                }}
              </p>
              <p class="mb-0 whitespace-pre-wrap">{{ item.reopen_reason }}</p>
            </div>
          </article>

          <p v-if="!resolutions.length" class="mb-0 text-sm text-n-slate-10">
            {{ $t('TICKET_LIFECYCLE.NO_RESOLUTIONS') }}
          </p>
        </section>

        <section class="space-y-3">
          <h3 class="m-0 text-sm font-semibold text-n-slate-12">
            {{ $t('TICKET_LIFECYCLE.CASE_HISTORY') }}
          </h3>
          <ol class="m-0 space-y-3 p-0">
            <li v-for="event in events" :key="event.id" class="flex gap-3">
              <span class="mt-1.5 size-2 shrink-0 rounded-full bg-n-slate-7" />
              <div class="min-w-0">
                <p class="mb-0 text-sm text-n-slate-11">
                  {{ eventDescription(event) }}
                </p>
                <p class="mb-0 mt-0.5 text-xs text-n-slate-9">
                  {{ formatTimestamp(event.created_at) }}
                </p>
              </div>
            </li>
          </ol>
        </section>
      </div>

      <footer class="flex justify-end gap-2 border-t border-n-weak px-5 py-4">
        <Button
          :label="$t('TICKET_LIFECYCLE.CANCEL')"
          variant="ghost"
          color="slate"
          type="button"
          @click="emit('close')"
        />
        <Button
          :label="primaryLabel"
          :disabled="!canSave"
          :is-loading="isSaving"
          type="button"
          @click="save"
        />
      </footer>
    </section>
  </div>
</template>
